import { SERVICES } from "@/lib/services";
import { cn } from "@/lib/utils";
import crsOffice1280 from "@/assets/hero/crs-office-1280.webp";
import cavityMasonry from "@/assets/services/cavity-wall-masonry-800.webp";
import wallTiesEvidence from "@/assets/remedial-wall-ties/walltie-evidence.webp";
import wallTiesBorescope from "@/assets/remedial-wall-ties/walltie-borescope.webp";

/**
 * Homepage hero decorative image layer.
 *
 * A slow, continuous marquee of service imagery drifting horizontally behind
 * the hero copy. Two rows scroll in opposite directions at deliberately
 * mismatched durations so cards never re-align or "beat" against each other.
 * Edges fade to transparent so the strip reads as ambient background, not a
 * UI carousel.
 *
 * Non-interactive: pointer-events-none, aria-hidden. Respects
 * prefers-reduced-motion by holding still.
 */

const SERVICE_CARDS = SERVICES.map((s) => ({
  slug: s.slug,
  src: s.hero800 ?? s.hero,
}));

// Extras curated from around the site so the strip isn't just service cards.
const EXTRA_CARDS = [
  { slug: "crs-office", src: crsOffice1280 },
  { slug: "cavity-masonry", src: cavityMasonry },
  { slug: "walltie-evidence", src: wallTiesEvidence },
  { slug: "walltie-borescope", src: wallTiesBorescope },
];

const HERO_CARDS = [...SERVICE_CARDS, ...EXTRA_CARDS];


export type HeroFloatImage = { src: string; slug?: string };

export function HeroServiceFloatOverlay({
  className,
  images,
}: {
  className?: string;
  /** Optional page-scoped image list. When omitted, uses the site-wide service card set. */
  images?: HeroFloatImage[];
}) {
  // Duplicate lists so each marquee row can loop seamlessly. Row 2 is offset
  // (rotated) so the two rows never display the same image directly above each
  // other, and reversed so imagery differs visually as well.
  const source: HeroFloatImage[] =
    images && images.length > 0
      ? images.map((img, i) => ({ src: img.src, slug: img.slug ?? `img-${i}` }))
      : HERO_CARDS;
  // Ensure enough tiles to fill the strip when the source is small.
  const padded = source.length >= 6 ? source : [...source, ...source, ...source, ...source];
  const rowA = [...padded, ...padded];
  const half = Math.floor(padded.length / 2);
  const rotated = [...padded.slice(half), ...padded.slice(0, half)].reverse();
  const rowB = [...rotated, ...rotated];

  return (
    <div
      className={cn(
        "hero-marquee-root absolute inset-0 z-[1] overflow-hidden pointer-events-none select-none",
        className,
      )}
      aria-hidden
    >
      <div className="hero-marquee-mask absolute inset-0 flex flex-col justify-center gap-6 md:gap-10">
        {/* Row 1 - drifts left */}
        <div className="hero-marquee-track hero-marquee-left flex w-max gap-4 md:gap-6">
          {rowA.map((card, i) => (
            <MarqueeCard key={`a-${card.slug}-${i}`} src={card.src} />
          ))}
        </div>
        {/* Row 2 - drifts right, smaller and dimmer for depth. */}
        <div className="hero-marquee-track hero-marquee-right flex w-max gap-4 md:gap-6 opacity-60">
          {rowB.map((card, i) => (
            <MarqueeCard key={`b-${card.slug}-${i}`} src={card.src} small />
          ))}
        </div>

      </div>



      <style>{`
        .hero-marquee-mask {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            #000 12%,
            #000 88%,
            transparent 100%
          );
                  mask-image: linear-gradient(
            to right,
            transparent 0%,
            #000 12%,
            #000 88%,
            transparent 100%
          );
        }
        @keyframes hero-marquee-left {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        @keyframes hero-marquee-right {
          from { transform: translate3d(-50%, 0, 0); }
          to   { transform: translate3d(0, 0, 0); }
        }
        .hero-marquee-track { will-change: transform; }
        .hero-marquee-left  { animation: hero-marquee-left  73s linear infinite; }
        .hero-marquee-right { animation: hero-marquee-right 97s linear infinite; }

        @media (prefers-reduced-motion: reduce) {
          .hero-marquee-left,
          .hero-marquee-right { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function MarqueeCard({ src, small = false }: { src: string; small?: boolean }) {
  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden rounded-2xl border border-white/15 shadow-soft",
        small
          ? "h-24 w-36 md:h-28 md:w-44"
          : "h-32 w-48 md:h-36 md:w-56",
      )}
    >
      <img
        src={src}
        alt=""
        width={224}
        height={144}
        sizes={small ? "176px" : "224px"}
        className="h-full w-full object-cover"
        loading="lazy"
        fetchPriority="low"
        decoding="async"
        draggable={false}
      />

      {/* Brand-tinted wash so cards read as calm background elements aligned
          with the site palette, not full-strength photographs. Lighter on
          mobile so the animation stays legible over the hero. */}
      <div className="absolute inset-0 bg-primary/20 md:bg-primary/35 mix-blend-multiply" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
    </div>
  );
}
